import {
  ALL_SURFACES,
  DEFAULT_SCHOOL_SECTIONS,
  FinderConfig,
  OptionDescription,
  SchoolFieldSection,
  SectionOption,
  SectionSurface,
} from '../types/finderConfig';
import { SCHOOL_TYPE_FIELD } from './finderFields';

// Choice-like field types that can drive a generated filter. Mirrors the admin.
const CHOICE_FIELD_TYPES = new Set(['checkbox', 'select', 'dropdown', 'radio', 'yes_no', 'multiselect']);

// Built-ins that can be filtered (seat availability has its own level filter).
const FILTERABLE_BUILTINS = new Set(['seat_availability']);

const normalizeSurfaces = (raw: unknown): SectionSurface[] => (
  Array.isArray(raw) ? ALL_SURFACES.filter((s) => raw.includes(s)) : []
);

const normalizeOptionDescriptions = (raw: unknown): OptionDescription[] | undefined => {
  if (!Array.isArray(raw)) return undefined;
  const list = raw
    .filter((o): o is Record<string, unknown> => !!o && typeof o === 'object')
    .filter((o) => typeof o.value === 'string' && o.value && typeof o.description === 'string' && o.description)
    .map((o) => ({ value: o.value as string, description: o.description as string }));
  return list.length ? list : undefined;
};

const normalizeOptions = (raw: unknown): SectionOption[] | undefined => {
  if (!Array.isArray(raw)) return undefined;
  const list = raw
    .filter((o): o is Record<string, unknown> => !!o && typeof o === 'object')
    .filter((o) => typeof o.value === 'string' && o.value && typeof o.label === 'string' && o.label)
    .map((o) => ({ value: o.value as string, label: o.label as string }));
  return list.length ? list : undefined;
};

const normalizeSection = (entry: unknown): SchoolFieldSection | null => {
  if (!entry || typeof entry !== 'object') return null;
  const e = entry as Record<string, unknown>;
  const source = e.source === 'builtin' ? 'builtin' : 'field';
  const field = typeof e.field === 'string' ? e.field : undefined;
  const key = typeof e.key === 'string' && e.key ? e.key : field;
  if (!key) return null;

  return {
    key,
    source,
    field: source === 'field' ? field ?? key : undefined,
    type: typeof e.type === 'string' ? e.type : undefined,
    label: typeof e.label === 'string' ? e.label : '',
    icon: typeof e.icon === 'string' ? e.icon : '',
    surfaces: normalizeSurfaces(e.surfaces),
    optionDescriptions: normalizeOptionDescriptions(e.optionDescriptions),
    options: normalizeOptions(e.options),
  };
};

/**
 * Narrow the wire value to valid section objects. The list is the source of
 * truth (empty array = hide all); only a missing/invalid field falls back to
 * the defaults.
 */
export const normalizeSchoolSections = (raw: unknown): SchoolFieldSection[] => {
  if (!Array.isArray(raw)) return DEFAULT_SCHOOL_SECTIONS;
  return raw.map(normalizeSection).filter((s): s is SchoolFieldSection => s !== null);
};

/** Ordered sections configured to appear on a given surface. */
export const sectionsForSurface = (
  config: FinderConfig,
  surface: SectionSurface,
): SchoolFieldSection[] => config.schoolSections.filter((s) => s.surfaces.includes(surface));

/** Whether a section can drive a generated filter (choice fields + seat built-in). */
export const isFilterableSection = (section: SchoolFieldSection): boolean => (
  section.source === 'builtin'
    ? FILTERABLE_BUILTINS.has(section.key)
    : CHOICE_FIELD_TYPES.has((section.type ?? '').toLowerCase())
);

/** Whether any configured section maps the given SchoolFinderV3 field (any surface). */
export const hasFieldSection = (config: FinderConfig, field: string): boolean => (
  config.schoolSections.some((s) => s.source === 'field' && s.field === field)
);

/** Whether any configured section uses the given built-in (any surface). */
export const hasBuiltinSection = (config: FinderConfig, key: string): boolean => (
  config.schoolSections.some((s) => s.source === 'builtin' && s.key === key)
);

/** Configured school-type option values (admin order); color-map source. */
export const schoolTypeOptionValues = (config: FinderConfig): string[] => (
  config.schoolSections
    .find((s) => s.source === 'field' && s.field === SCHOOL_TYPE_FIELD)
    ?.options?.map((o) => o.value) ?? []
);
